import type {RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {RTNode} from '@prismicio/types'
import type {AuthorDocumentData, GlossaryTermDocumentData} from 'prismicio-types'
import type {ArticleStructuredData} from 'slices/BlogPost/utils'
import {DEFAULT_AUTHOR_NAME, LOGO_URL, sanitizeJsonLd} from 'slices/BlogPost/utils'

/**
 * Heading extracted from content
 */
export type Heading = {
  id: string
  text: string
}

/**
 * Creates a URL-friendly slug from text
 */
export const createSlugFromText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

/**
 * Extracts all h3 headings from rich text content for table of contents
 * Matches BlogPost heading extraction (h3 only)
 */
export const extractHeadingsFromDefinition = (content: RTNode[] | null | undefined): Heading[] => {
  const headings: Heading[] = []

  if (!content) {
    return headings
  }

  content.forEach((node: RTNode) => {
    if (node.type === 'heading3' && 'text' in node && node.text) {
      headings.push({
        id: createSlugFromText(node.text),
        text: node.text,
      })
    }
  })

  return headings
}

/**
 * Generates structured data (JSON-LD) for a glossary term page
 */
export const generateGlossaryStructuredData = (params: {
  term: RichTextField
  imageUrl: string
  author: GlossaryTermDocumentData['author']
  publishedDate: string | null | undefined
  lastUpdatedDate: string | null | undefined
}): ArticleStructuredData | null => {
  const {term, imageUrl, author, publishedDate, lastUpdatedDate} = params

  const firstNode = term?.[0]
  const headline = isFilled.richText(term) && firstNode && 'text' in firstNode ? firstNode.text : ''

  const authorData = isFilled.contentRelationship(author) && author.data ? (author.data as AuthorDocumentData) : null
  const authorName = authorData?.name || DEFAULT_AUTHOR_NAME

  const datePublished = publishedDate ? new Date(publishedDate).toISOString() : ''

  if (!headline || !datePublished) {
    return null
  }

  const dateModified = lastUpdatedDate ? new Date(lastUpdatedDate).toISOString() : datePublished

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    ...(imageUrl ? {image: [imageUrl]} : {}),
    datePublished,
    dateModified,
    author: {
      '@type': 'Person',
      name: authorName,
      ...(authorData?.photo.url && {image: authorData.photo.url}),
    },
    publisher: {
      '@type': 'Organization',
      name: DEFAULT_AUTHOR_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
  }
}

export {sanitizeJsonLd}
